import styles from 'legacy/common/components/form/Form.module.scss';

import React, { useState } from 'react';
import classnames from 'classnames';
import ReactCodeInput from 'react-verification-code-input';
import { useTranslation } from 'react-i18next';

import { useResendLoginVerificationCodeCommand } from 'legacy/auth/hooks';

import { VerificationSessionDto } from 'legacy/auth/types';

import FormWrapper from '../FormWrapper';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import SendConfirmationCodeToEmailModal from './SendConfirmationCodeToEmailModal';
import * as toast from 'legacy/common/utils/toast';

type LoginConfirmationProps = {
  verificationSession: VerificationSessionDto;
  onBackClick: () => void;
  onSubmit: (code: string) => void;
  isLoading: boolean;
  errorMessage?: string;
};

export const LoginConfirmationModalContent: React.FC<
  LoginConfirmationProps
> = ({
  verificationSession,
  onBackClick,
  onSubmit,
  isLoading,
  errorMessage
}) => (
  <LoginConfirmation
    verificationSession={verificationSession}
    onBackClick={onBackClick}
    onSubmit={onSubmit}
    isLoading={isLoading}
    errorMessage={errorMessage}
    isModal
  />
);

const LoginConfirmation: React.FC<
  LoginConfirmationProps & { isModal?: boolean }
> = ({
  verificationSession,
  onBackClick,
  onSubmit,
  isLoading,
  errorMessage,
  isModal = false
}) => {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');
  const [
    showSendConfirmationCodeToEmailModal,
    setShowSendConfirmationCodeToEmailModal
  ] = React.useState(false);
  const [showSendAgainButton, setShowSendAgainButton] = React.useState(true);

  // Determine default delivery method if not provided
  const getDefaultDeliveryMethod = (): 'email' | 'sms' => {
    if (verificationSession.deliveryMethod) {
      return verificationSession.deliveryMethod;
    }
    // Default to email if available, otherwise SMS
    if (verificationSession.obfuscatedEmail) {
      return 'email';
    }
    return 'sms';
  };

  const [currentDeliveryMethod, setCurrentDeliveryMethod] = React.useState<
    'email' | 'sms'
  >(getDefaultDeliveryMethod());

  const resendLoginVerificationCode = useResendLoginVerificationCodeCommand();

  const isEmailDelivery = currentDeliveryMethod === 'email';
  const isSmsDelivery = currentDeliveryMethod === 'sms';

  const handleOnClickSendAgain = () => {
    resendLoginVerificationCode.mutate(
      {
        verificationSessionId: verificationSession.verificationSessionId,
        deliveryMethod: currentDeliveryMethod
      },
      {
        onSuccess: () => {
          if (isEmailDelivery) {
            toast.success(
              t('confirmation.confirmationCodeSentToEmail', {
                email: verificationSession.obfuscatedEmail
              })
            );
          } else {
            toast.success(
              t('confirmation.confirmationCodeSent', {
                phone: verificationSession.obfuscatedPhone
              })
            );
          }
        }
      }
    );
  };

  const getSubtitle = () => {
    if (isEmailDelivery && verificationSession.obfuscatedEmail) {
      return t('confirmation.loginSubtitleWithEmail', {
        email: verificationSession.obfuscatedEmail
      });
    }
    if (isSmsDelivery && verificationSession.obfuscatedPhone) {
      return t('confirmation.loginSubtitleWithPhone', {
        phone: verificationSession.obfuscatedPhone
      });
    }

    const method = isEmailDelivery
      ? t('confirmation.methodEmail')
      : t('confirmation.methodPhone');
    return t('confirmation.loginSubtitle', { method });
  };

  const handleSwitchDeliveryMethod = () => {
    const newDeliveryMethod = isEmailDelivery ? 'sms' : 'email';

    resendLoginVerificationCode.mutate(
      {
        verificationSessionId: verificationSession.verificationSessionId,
        deliveryMethod: newDeliveryMethod
      },
      {
        onSuccess: () => {
          setCurrentDeliveryMethod(newDeliveryMethod);
          if (newDeliveryMethod === 'email') {
            toast.success(
              t('confirmation.confirmationCodeSentToEmail', {
                email: verificationSession.obfuscatedEmail
              })
            );
          } else {
            toast.success(
              t('confirmation.confirmationCodeSent', {
                phone: verificationSession.obfuscatedPhone
              })
            );
          }
          setShowSendAgainButton(false);
        }
      }
    );
  };

  return (
    <FormWrapper
      title={t('confirmation.title')}
      showBack
      onBackClick={onBackClick}
      subtitle={getSubtitle()}
      isModal={isModal}
    >
      <div className={styles.group}>
        <p className={styles.submitError}>
          {errorMessage || resendLoginVerificationCode.error?.message}
        </p>
      </div>
      <div className={styles.group} data-testid="form-confirmation">
        <p className={classnames(styles.label, styles.small)}>
          {t('confirmation.verificationCodeLabel')}
        </p>
        <div
          className={`${styles.codeInputContainer} [&_input]:font-family-inter!`}
        >
          <ReactCodeInput
            fields={4}
            fieldWidth={45}
            fieldHeight={60}
            onComplete={(val) => {
              onSubmit(val);
              setCode(val);
            }}
            className={styles.codeInput}
            placeholder={['#', '#', '#', '#']}
          />
        </div>
      </div>
      <div className={styles.FormGroup}>
        <Button
          disabled={!code}
          loading={isLoading}
          type={ButtonType.Primary}
          onClick={() => {
            onSubmit(code);
          }}
        >
          {t('confirmation.confirmCodeButton')}
        </Button>

        {showSendAgainButton && (
          <div className="mt-4">
            <span className="mr-1 inline-block">
              {t('confirmation.didntReceive')}
            </span>
            {resendLoginVerificationCode.isLoading ? (
              <span>{t('confirmation.sendingAgain')}</span>
            ) : (
              <Button onClick={handleOnClickSendAgain} type={ButtonType.Text}>
                {t('confirmation.sendAgain')}
              </Button>
            )}
          </div>
        )}

        {isEmailDelivery && verificationSession.obfuscatedPhone && (
          <div className="mt-4">
            <Button
              onClick={handleSwitchDeliveryMethod}
              type={ButtonType.Text}
              disabled={resendLoginVerificationCode.isLoading}
            >
              {t('confirmation.cantAccessEmail')}
            </Button>
          </div>
        )}

        {isSmsDelivery && verificationSession.obfuscatedEmail && (
          <div className="mt-4">
            <Button
              onClick={handleSwitchDeliveryMethod}
              type={ButtonType.Text}
              disabled={resendLoginVerificationCode.isLoading}
            >
              {t('confirmation.useEmailInstead')}
            </Button>
          </div>
        )}
      </div>

      {showSendConfirmationCodeToEmailModal && (
        <SendConfirmationCodeToEmailModal
          onSendConfirmationCodeSuccess={() => setShowSendAgainButton(false)}
          onClose={() => setShowSendConfirmationCodeToEmailModal(false)}
          verificationSession={verificationSession}
        />
      )}
    </FormWrapper>
  );
};

export default LoginConfirmation;
